import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { MpnMapping, UpdateMpnMappingRequest } from '@models/csp/mpn-mapping.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  createCustomStateSetOnUrlHandler,
  createStateResetHandler,
  deleteEntity,
  getAll,
  StateDictionary,
  updateEntity,
} from '../../helpers';
import { MpnMappingsService } from './mpn-mappings.service';

type MpnMappingsState = {
  mpnMappings: MpnMapping[];
  isLoading: boolean;
  selectedMappingId: string | null;
};

const initialState: MpnMappingsState = {
  mpnMappings: [],
  isLoading: false,
  selectedMappingId: null,
};

export const mpnMappingsDictionary: StateDictionary<MpnMapping> = {
  entityList: 'mpnMappings',
  listLoading: 'isLoading',
  createLoading: '',
  selectedEntity: '',
  selectedEntityLoading: 'isLoading',
  entityId: 'mpnId',
};

export const MpnMappingsStore = signalStore(
  withState(initialState),
  withDevtools('mpn-mappings-store'),
  withComputed(state => ({
    selectedMapping: computed(() => {
      const id = state.selectedMappingId();
      return id ? (state.mpnMappings().find(m => m.mpnId === id) ?? null) : null;
    }),
  })),
  withMethods((store, mpnMappingsService = inject(MpnMappingsService), router = inject(Router)) => {
    createStateResetHandler<MpnMappingsState>('/csp/mpn-mappings')(store, initialState);
    createCustomStateSetOnUrlHandler<MpnMappingsState>('/csp/mpn-mappings', { selectedMappingId: null })(store);

    const repositoryMethods = {
      getAll: getAll<MpnMapping>({
        store,
        service: mpnMappingsService,
        dictionary: mpnMappingsDictionary,
      }),

      updateMpnMapping: updateEntity<MpnMapping, UpdateMpnMappingRequest>({
        store,
        service: mpnMappingsService,
        dictionary: mpnMappingsDictionary,
      }),

      deleteMpnMapping: deleteEntity<MpnMapping>({
        store,
        service: mpnMappingsService,
        dictionary: mpnMappingsDictionary,
        onSuccessCallback: () => {
          patchState(store, { selectedMappingId: null });
          router.navigate(['/csp/mpn-mappings']);
        },
      }),
    };

    const navigationActions = {
      selectMapping: (mpnId: string): void => {
        if (!store.mpnMappings().length && !store.isLoading()) repositoryMethods.getAll();
        patchState(store, { selectedMappingId: mpnId });
      },

      goToListPage: (): void => {
        patchState(store, { selectedMappingId: null });
        router.navigate(['/csp/mpn-mappings']);
      },

      goToMappingActions: async (mpnId: string): Promise<boolean> => {
        const result = await router.navigate(['/csp/mpn-mappings', mpnId]);
        if (!result) return false;
        patchState(store, { selectedMappingId: mpnId });
        return true;
      },

      goToMappingMpnSubscriptions: (mpnId: string): void => {
        router.navigate(['/csp/mpn-mappings', mpnId, 'csp-subscriptions']);
      },
    };

    return {
      ...repositoryMethods,
      ...navigationActions,
    };
  }),
);

export interface MpnMappingsStoreType {
  readonly mpnMappings: Signal<MpnMapping[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedMappingId: Signal<string | null>;
  readonly selectedMapping: Signal<MpnMapping | null | undefined>;

  readonly getAll: () => void;
  readonly updateMpnMapping: (input: { id: string; dto: UpdateMpnMappingRequest }) => void;
  readonly deleteMpnMapping: (mpnId: string) => void;
  readonly selectMapping: (mpnId: string) => void;
  readonly goToListPage: () => void;
  readonly goToMappingActions: (mpnId: string) => Promise<boolean>;
  readonly goToMappingMpnSubscriptions: (mpnId: string) => void;
}
